import React from 'react';
import { CloudRain, Droplets, Flame, Waves, Moon, Bird, Sparkles, Headphones, Play, Pause } from 'lucide-react';
import { useSoundPlayer } from '../../context/SoundPlayerContext';

// Real 3-minute recorded nature soundscapes from Internet Archive (public
// domain / creative commons), trimmed with ffmpeg to seamless 180-sec segments.
// Baked as static MP3s so playback starts instantly. This is what real
// meditation apps use — recorded field audio, not synth.
const AUDIO_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/audio/soundscapes`;
const url = (slug) => `${AUDIO_BASE}/${slug}.mp3?v=2`;

const CATALOG = {
  mental: {
    accent: '#C08CFF',
    tracks: [
      { id: 'forest-rain',     name: 'Forest Ambience', intent: 'De-stress', desc: 'Tall trees, wind through leaves, distant birds', icon: CloudRain, color: '#7FE0A8', url: url('forest-rain') },
      { id: 'alpine-stream',   name: 'Alpine Stream',   intent: 'Focus',     desc: 'A babbling brook flowing over stones', icon: Droplets, color: '#5BDCF5', url: url('alpine-stream') },
      { id: 'fireside',        name: 'Fireside',        intent: 'Ground',    desc: 'Crackling wood fire — nervous system down-shift', icon: Flame, color: '#F59E0B', url: url('fireside') },
      { id: 'forest-birdsong', name: 'Forest Birdsong', intent: 'Uplift',    desc: 'Water, birds, frogs — a living forest morning', icon: Bird, color: '#C08CFF', url: url('forest-birdsong') },
    ],
  },
  recovery: {
    accent: '#5BDCF5',
    tracks: [
      { id: 'ocean-dusk',    name: 'Ocean at Dusk',    intent: 'Recover', desc: 'Slow tide rolling in — parasympathetic reset', icon: Waves, color: '#5BDCF5', url: url('ocean-dusk') },
      { id: 'sleep-rain',    name: 'Rainstorm',        intent: 'Sleep',   desc: 'Steady rain with distant rolling thunder', icon: CloudRain, color: '#9AA8FF', url: url('sleep-rain') },
      { id: 'night-forest',  name: 'Night Forest',     intent: 'Drift',   desc: 'Crickets, owls, wind — a deep-woods night', icon: Moon, color: '#7FA0E0', url: url('night-forest') },
      { id: 'ambient-drone', name: 'Tibetan Bowls',    intent: 'Unwind',  desc: 'Resonant singing bowls — meditation companion', icon: Sparkles, color: '#C08CFF', url: url('ambient-drone') },
    ],
  },
};

export default function SoundShelf({ pillar = 'mental' }) {
  const cfg = CATALOG[pillar] || CATALOG.mental;
  const { active, launch } = useSoundPlayer();
  return (
    <div data-testid="sound-shelf">
      <div className="flex items-center gap-2 mb-1">
        <Headphones className="w-4 h-4" style={{ color: cfg.accent }} />
        <span className="text-[10px] uppercase tracking-[0.2em] font-secondary font-bold" style={{ color: cfg.accent }}>Sounds</span>
      </div>
      <h3 className="font-secondary font-bold text-xl text-white">What do you need right now?</h3>
      <p className="text-xs text-zinc-500 mb-4 flex items-center gap-1.5"><Headphones className="w-3 h-3" /> Real recorded soundscapes · 3-min loops · plays with the screen off</p>

      <div className="grid sm:grid-cols-2 gap-3">
        {cfg.tracks.map((x) => {
          const Icon = x.icon;
          const on = active?.id === x.id;
          return (
            <button
              key={x.id}
              onClick={() => launch(x, pillar)}
              data-testid={`sound-exp-${x.id}`}
              className="relative p-4 rounded-2xl text-left border backdrop-blur-xl transition-all duration-200 hover:scale-[1.02] active:scale-95"
              style={{ borderColor: on ? x.color : `${x.color}33`, background: on ? `${x.color}1a` : 'rgba(0,0,0,0.4)', boxShadow: on ? `0 0 22px ${x.color}40` : 'none' }}
            >
              <span className="flex items-center justify-between">
                <span className="w-9 h-9 rounded-full grid place-items-center" style={{ background: `${x.color}22`, border: `1px solid ${x.color}55` }}>
                  <Icon className="w-4 h-4" style={{ color: x.color }} />
                </span>
                <span className="text-[9px] font-bold uppercase tracking-widest" style={{ color: x.color }}>{x.intent}</span>
              </span>
              <h4 className="font-secondary font-bold text-base text-white mt-3">{x.name}</h4>
              <p className="text-xs text-zinc-400 mt-0.5 leading-snug">{x.desc}</p>
              <span className="mt-2 inline-flex items-center gap-1 text-[11px]" style={{ color: x.color }}>
                {on ? <><Pause className="w-3 h-3" /> playing</> : <><Play className="w-3 h-3" /> play</>}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
